import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, ChevronLeft, ChevronRight, X } from "lucide-react";
import { setSeo, track } from "@/lib/api";
import { AffiliateDisclosure } from "@/components/site/OfferGrid";
import {
  SAUNACLOUD_GALLERY,
  SAUNACLOUD_URL,
  galleryImage,
} from "@/data/saunacloudGallery";

const PartnerCta = ({ placement, testId }) => (
  <a
    href={SAUNACLOUD_URL}
    target="_blank"
    rel="sponsored nofollow noopener"
    data-testid={testId}
    onClick={() =>
      track({
        event_type: "cta_click",
        cta_placement: placement,
        offer_category: "custom-saunas",
        merchant: "SaunaCloud",
      })
    }
    className="inline-flex w-full sm:w-auto items-center justify-center gap-2.5 px-8 py-4 bg-sage text-white text-[12.5px] tracking-[0.14em] uppercase font-semibold hover:bg-sage-dark transition-colors duration-300"
  >
    Explore SaunaCloud <ArrowUpRight size={16} />
  </a>
);

const Lightbox = ({ index, onClose, onStep }) => {
  const item = SAUNACLOUD_GALLERY[index];
  const touchX = useRef(null);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") onStep(1);
      if (e.key === "ArrowLeft") onStep(-1);
    };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose, onStep]);

  return (
    <div
      data-testid="gallery-lightbox"
      role="dialog"
      aria-modal="true"
      aria-label={item.alt}
      className="fixed inset-0 z-[120] flex items-center justify-center p-4 md:p-10"
      style={{ backgroundColor: "#0A0A0A" }}
      onClick={onClose}
      onTouchStart={(e) => {
        touchX.current = e.touches[0].clientX;
      }}
      onTouchEnd={(e) => {
        if (touchX.current === null) return;
        const dx = e.changedTouches[0].clientX - touchX.current;
        if (Math.abs(dx) > 45) onStep(dx < 0 ? 1 : -1);
        touchX.current = null;
      }}
    >
      <img
        src={galleryImage(item.base, 1600)}
        alt={item.alt}
        width={item.w}
        height={item.h}
        data-testid="gallery-lightbox-image"
        className="max-h-full max-w-full object-contain"
        onClick={(e) => e.stopPropagation()}
      />
      <button
        type="button"
        aria-label="Close gallery"
        data-testid="gallery-lightbox-close"
        onClick={onClose}
        className="absolute top-4 right-4 md:top-6 md:right-7 p-3 text-white/85 hover:text-white transition-colors"
      >
        <X size={26} />
      </button>
      <button
        type="button"
        aria-label="Previous photograph"
        data-testid="gallery-lightbox-prev"
        onClick={(e) => {
          e.stopPropagation();
          onStep(-1);
        }}
        className="absolute left-2 md:left-6 p-3 md:p-4 text-white/80 hover:text-white transition-colors"
      >
        <ChevronLeft size={34} />
      </button>
      <button
        type="button"
        aria-label="Next photograph"
        data-testid="gallery-lightbox-next"
        onClick={(e) => {
          e.stopPropagation();
          onStep(1);
        }}
        className="absolute right-2 md:right-6 p-3 md:p-4 text-white/80 hover:text-white transition-colors"
      >
        <ChevronRight size={34} />
      </button>
      <span
        data-testid="gallery-lightbox-counter"
        className="absolute bottom-5 left-1/2 -translate-x-1/2 text-[12px] tracking-[0.18em] uppercase text-white/70"
      >
        {index + 1} / {SAUNACLOUD_GALLERY.length}
      </span>
    </div>
  );
};

export default function SaunaCloudGallery() {
  const [open, setOpen] = useState(null);

  useEffect(() => {
    setSeo({
      title: "SaunaCloud | Custom Sauna Inspiration Gallery — Human Home Living",
      description:
        "Browse SaunaCloud promotional photography: custom sauna interiors, glass-fronted cabins, infrared and red light installations, and outdoor sauna designs for home wellness spaces.",
    });
    track({ event_type: "page_view", page: "/saunacloud-gallery" });
  }, []);

  const step = useCallback((dir) => {
    setOpen((i) =>
      i === null ? i : (i + dir + SAUNACLOUD_GALLERY.length) % SAUNACLOUD_GALLERY.length
    );
  }, []);

  return (
    <div data-testid="saunacloud-gallery-page">
      <section className="px-5 md:px-8 pt-12 md:pt-16 pb-10 md:pb-12 max-w-[1280px] mx-auto">
        <Link
          to="/hub/wellness-recovery"
          data-testid="gallery-back-link"
          className="inline-flex items-center gap-2 text-[13px] text-[#5C584F] hover:text-charcoal transition-colors"
        >
          <ArrowLeft size={15} /> Back to Wellness &amp; Recovery
        </Link>
        <span className="eyebrow block mt-7">SaunaCloud</span>
        <h1 className="display text-[34px] md:text-[52px] leading-[1.05] text-charcoal mt-3 max-w-3xl">
          Custom Sauna Inspiration Gallery
        </h1>
        <p className="text-[17px] md:text-[18px] leading-[1.65] text-[#454545] mt-5 max-w-2xl">
          SaunaCloud promotional photography of custom sauna spaces — glass-fronted cabins, infrared
          and red light interiors, bench and joinery detail, and outdoor installations. Use them as
          design inspiration for your own project.
        </p>
        <div className="mt-8">
          <PartnerCta placement="GALLERY TOP CTA" testId="gallery-cta-top" />
          <AffiliateDisclosure testId="page-affiliate-disclosure" className="mt-4 max-w-2xl" />
        </div>
      </section>

      <section className="px-5 md:px-8 pb-14 md:pb-20 max-w-[1280px] mx-auto">
        <div
          data-testid="saunacloud-gallery-grid"
          className="columns-1 sm:columns-2 lg:columns-3 gap-4 md:gap-5 [column-fill:_balance]"
        >
          {SAUNACLOUD_GALLERY.map((item, i) => (
            <button
              key={item.base}
              type="button"
              onClick={() => setOpen(i)}
              aria-label={`Enlarge photograph: ${item.alt}`}
              data-testid={`gallery-item-${item.base}`}
              className="group mb-4 md:mb-5 block w-full break-inside-avoid overflow-hidden bg-bone focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage focus-visible:ring-offset-2"
            >
              <img
                src={galleryImage(item.base, 800)}
                srcSet={`${galleryImage(item.base, 800)} 800w, ${galleryImage(item.base, 1600)} 1600w`}
                sizes="(min-width:1024px) 33vw, (min-width:640px) 50vw, 100vw"
                alt={item.alt}
                width={item.w}
                height={item.h}
                loading={i < 3 ? "eager" : "lazy"}
                decoding="async"
                className="w-full h-auto block transition-transform duration-[900ms] ease-out group-hover:scale-[1.03]"
              />
            </button>
          ))}
        </div>

        <div className="mt-12 md:mt-16 pt-10 border-t border-line">
          <h2 className="display text-[24px] md:text-[30px] text-charcoal max-w-2xl leading-[1.15]">
            Ready to design your own sauna?
          </h2>
          <p className="text-[16.5px] leading-[1.65] text-[#454545] mt-3 max-w-2xl">
            SaunaCloud builds custom infrared saunas around your space. Explore their options, then
            come back to compare the rest of your wellness shortlist.
          </p>
          <div className="mt-7">
            <PartnerCta placement="GALLERY BOTTOM CTA" testId="gallery-cta-bottom" />
          </div>
          <Link
            to="/hub/wellness-recovery"
            data-testid="gallery-back-link-bottom"
            className="inline-flex items-center gap-2 text-[13px] text-[#5C584F] hover:text-charcoal transition-colors mt-6"
          >
            <ArrowLeft size={15} /> Back to Wellness &amp; Recovery
          </Link>
        </div>
      </section>

      {open !== null && (
        <Lightbox index={open} onClose={() => setOpen(null)} onStep={step} />
      )}
    </div>
  );
}
